'use client';
import { useEngineerIdentity } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { AnimatedSwitch } from '@/components/ui/animated-switch';
import { NavLink } from '@/components/ui/navigation-list';
import { useNetworkConfig } from '@/lib/network/use-network-config';
import { logger } from '@/lib/telemetry/logger';
import { useFundingDevtoolsStore } from '@/stores/funding-devtools';
import { useFundingStage } from '../shared/funding-chrome';
import { FundingSandboxCustomer } from './funding-sandbox-customer';
import { useFundingLocationOverride } from './use-funding-location-override';

export function FundingDevtoolsPage(): ReactNode {
  const t = useTranslations('onramp.devtools');
  return (
    <main className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-6">
      <h1 className="text-heading-lg">{t('title')}</h1>
      <FundingDevtoolsControls />
    </main>
  );
}

export function FundingDevtoolsScreen({
  onBack,
  sandboxCustomer,
}: {
  onBack(): void;
  sandboxCustomer?: Parameters<typeof FundingSandboxCustomer>[0];
}): ReactNode {
  const t = useTranslations('onramp.devtools');
  useFundingStage(t('title'), false, onBack);
  return (
    <div className="flex flex-col gap-5">
      <FundingDevtoolsControls />
      {sandboxCustomer && <FundingSandboxCustomer {...sandboxCustomer} />}
    </div>
  );
}

function FundingDevtoolsControls(): ReactNode {
  const t = useTranslations('onramp.devtools');
  const { user } = useEngineerIdentity();
  const { network } = useNetworkConfig();
  const enabled = useFundingLocationOverride();
  const setLocationOverride = useFundingDevtoolsStore((state) => state.setLocationOverride);
  return (
    <>
      <section className="flex flex-col gap-3 rounded-control bg-background-tertiary p-4">
        <div className="flex items-center justify-between gap-4">
          <h2 id="funding-location-override-label" className="text-heading-sm">
            {t('location_override')}
          </h2>
          <AnimatedSwitch
            aria-labelledby="funding-location-override-label"
            checked={enabled}
            disabled={network === 'mainnet'}
            onCheckedChange={(value: boolean): void => {
              if (!user || network === 'mainnet') return;
              setLocationOverride(user.id, value);
              logger.info('onramp.devtools_location_override_changed', { enabled: value, network });
            }}
          />
        </div>
        <p className="text-caption text-foreground-secondary">
          {network === 'mainnet' ? t('sandbox_only') : t('description')}
        </p>
      </section>
      <NavLink href="/funding/fixtures" prefetch={false}>
        {t('fixtures')}
      </NavLink>
    </>
  );
}
